// Role / people helpers. DB-side RLS is the real source of truth;
// these helpers exist so the UI can hide affordances ahead of the round-trip.
//
// Phase E (§I11): prefer JWT-claim permission checks
// (`hasPermission`, `canAdmin`, etc.) over role-name matching.
// Legacy `userIsAdmin` / `userIsSuperadmin` kept as the second leg of
// a union check during the dual-read migration window — matches the
// governance SQL side (see 0067_permission_based_rls.sql).

import { findPerson } from '../../shared/functions/peopleUtils.js'

// ── Phase E JWT-claim readers ────────────────────────────────────
// Live claim name is `permissions` (matches `symbols-auth-bridge` +
// `jwt_has_perm` helper). TASKS_NEEDED §62 mentions `active_permissions`
// — that's documentation drift; code authoritative.

const _claims = (root) => (root && root.jwtClaims) || {}
export const currentPermissions = (root) => _claims(root).permissions || []

// True if the caller has the given permission key OR is an owner.
// Owner shortcut mirrors the SQL helper's owner-bypass.
export function hasPermission (root, perm) {
  const r = root != null ? root : (this && this.state && this.state.root)
  if (!r) return false
  if (_claims(r).is_owner) return true
  return currentPermissions(r).includes(perm)
}

export function hasRole (user, role) {
  // CU-10 §90: roles are no longer on the person object. They live in
  // root.roles.byUser[userId]. We check that instead.
  if (!user) return false
  // Legacy path: if person still has a .roles array (pre-migration client),
  // honour it during the transition window.
  if (Array.isArray(user.roles) && user.roles.length > 0) {
    return user.roles.includes(role)
  }
  // New path: look up the userId in root.roles.byUser via the closure
  // on `this.state.root` (available when called via el.call).
  const root = this && this.state && this.state.root
  if (!root || !user.userId) return false
  const assignedRole = (root.roles?.byUser || {})[user.userId]
  return assignedRole === role
}

export function userIsSuperadmin (user) {
  return hasRole(user, 'superadmin')
}

export function userIsAdmin (user) {
  return hasRole(user, 'superadmin') || hasRole(user, 'admin')
}

export function isExternalEmail (email, people = []) {
  if (!email) return true
  return !people.find(p => p.email === email)
}

export function currentUser (root) {
  const r = root != null ? root : (this && this.state && this.state.root)
  return r ? findPerson(r, r.currentUserEmail) : null
}

export function currentUserIsAdmin (root) {
  const r = root != null ? root : (this && this.state && this.state.root)
  if (!r) return false
  // JWT-based ownership trumps the Mongo `roles.byUser` lookup. The auth
  // gates on /admin/* pages use JWT claims (isOwner / org_role / perms),
  // so the dropdown discovery link must follow the same rule — otherwise
  // legitimate owners can reach the pages directly but never see the
  // entry point. Mongo `roles.byUser` is org-membership-scoped and not
  // always seeded for owners on every org.
  const c = r.jwtClaims
  if (c?.is_owner === true) return true
  if (c?.org_role === 'admin' || c?.org_role === 'owner') return true
  if (c?.workspace_role === 'admin' || c?.workspace_role === 'owner') return true
  return userIsAdmin(currentUser(r))
}

export function currentUserIsSuperadmin (root) {
  const r = root != null ? root : (this && this.state && this.state.root)
  return userIsSuperadmin(currentUser(r))
}

// ── el.call()-friendly permission checks ──────────────────────────
// Via el.call: el.call('isAdmin') — no args needed, root from this.state.root.
// Via direct: isAdmin(s) — pass component state, root from s.root.
// All use regular function (not arrow) so `this` binds when called via el.call.

export function isAdmin (s) {
  const root = s?.root || (this && this.state && this.state.root)
  return userIsAdmin(currentUser(root))
}

// Walk up the element tree to the nearest ancestor with a `routes` map —
// the page-level router root that `el.router(path, root)` needs.
export function findRouteRoot (startEl) {
  let cur = startEl || this
  while (cur && !cur.routes) cur = cur.parent
  return cur || null
}

// Cold-load-safe admin gate. Returns true when the page should redirect.
// Bails (returns false) until both `currentUserId` and the `people` row set
// are seeded — otherwise findPerson would falsely report "not admin"
// during initial hydration and bounce the user away.
export function shouldRedirectNonAdmin (s, role) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root?.currentUserId) return false
  if (!Array.isArray(root.people) || !root.people.length) return false
  const checker = role === 'superadmin' ? userIsSuperadmin : userIsAdmin
  // Phase E union: permission-matching leg first so JWT-carried owners
  // and perm-granted users pass without needing a Supabase `user_roles`
  // row. Legacy role check remains as the second leg during the
  // dual-read window.
  const permKey = role === 'superadmin' ? 'admin.danger.access' : 'admin.pages.read'
  if (hasPermission(root, permKey)) return false
  return !checker(currentUser(root))
}

// Phase E — permission-gated page guard. Same cold-load semantics as
// `shouldRedirectNonAdmin` but checks a specific permission key instead
// of role names. Preferred going forward.
export function shouldRedirectNoPerm (s, perm) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root?.currentUserId) return false
  // Owner/perm path works before `people` is seeded; only fall back to
  // the legacy branch if we *have* to.
  if (_claims(root).is_owner) return false
  if (hasPermission(root, perm)) return false
  if (!Array.isArray(root.people) || !root.people.length) return false
  // Final legacy leg — `admin.pages.read` maps to admin; explicit perms
  // not in the claim fall through to role-name check so users with
  // `user_roles` entries but no Phase E JWT don't lose access.
  if (perm === 'admin.pages.read') return !userIsAdmin(currentUser(root))
  if (perm === 'admin.danger.access') return !userIsSuperadmin(currentUser(root))
  return true
}

export function isSuperAdmin (s) {
  const root = s?.root || (this && this.state && this.state.root)
  return userIsSuperadmin(currentUser(root))
}

export function isManager (s) {
  const root = s?.root || (this && this.state && this.state.root)
  const user = currentUser(root)
  return hasRole(user, 'manager') || userIsAdmin(user)
}

// Phase E — permission-gated counterparts. Prefer these going forward;
// the `userIsAdmin`/`userIsSuperadmin` variants remain for the dual-read
// window so callers don't all migrate in one commit.
export function canAdmin (s) {
  const root = s?.root || (this && this.state && this.state.root)
  return hasPermission(root, 'admin.pages.read') || userIsAdmin(currentUser(root))
}

export function canSuperadmin (s) {
  const root = s?.root || (this && this.state && this.state.root)
  return hasPermission(root, 'admin.danger.access') || userIsSuperadmin(currentUser(root))
}

export function canManage (s) {
  const root = s?.root || (this && this.state && this.state.root)
  if (hasPermission(root, 'member.update_role')) return true
  const user = currentUser(root)
  return hasRole(user, 'manager') || userIsAdmin(user)
}

// Can view private data (equity, docs, salary) for a given person.
// Phase E: self OR `profile.private.read` perm OR legacy superadmin.
// el.call('canAccessPrivate', person) or canAccessPrivate(s, person)
export function canAccessPrivate (sOrPerson, maybePerson) {
  let root, person
  if (maybePerson !== undefined) {
    root = sOrPerson?.root || (this && this.state && this.state.root)
    person = maybePerson
  } else {
    root = this && this.state && this.state.root
    person = sOrPerson
  }
  if (!root) return true
  const isSelfUser = !person?.email || person.email === root.currentUserEmail
  if (isSelfUser) return true
  if (hasPermission(root, 'profile.private.read')) return true
  return userIsSuperadmin(currentUser(root))
}

// Can edit a person's profile fields.
// Phase E: self OR `profile.edit_others` perm OR legacy superadmin.
// el.call('canEditProfile', person) or canEditProfile(s, person)
export function canEditProfile (sOrPerson, maybePerson) {
  let root, person
  if (maybePerson !== undefined) {
    root = sOrPerson?.root || (this && this.state && this.state.root)
    person = maybePerson
  } else {
    root = this && this.state && this.state.root
    person = sOrPerson
  }
  if (!root) return false
  const isSelfUser = !person?.email || person.email === root.currentUserEmail
  if (isSelfUser) return true
  if (hasPermission(root, 'profile.edit_others')) return true
  return userIsSuperadmin(currentUser(root))
}

// True when the current user is a signed-in core team member.
// Used by AppHeader to show/hide the cross-app nav.
export function isActiveMember (s) {
  const root = s?.root || (this && this.state && this.state.root)
  if (!root || !root.signedIn) return false
  const people = root.people || []
  const email = root.currentUserEmail
  if (!email || !people.find(p => p.email === email)) return false
  // CU-10 §90: roles.available dropped with public.roles view (migration 0087).
  // Any signed-in org member is considered active regardless of role.
  return true
}

// Group a list of people by their role. Returns an ordered array of
// { role, label, color, users } objects. Available via el.call('groupByRole', people).
export function groupByRole (people) {
  const root = this && this.state && this.state.root
  const available = root?.roles?.available || []
  // CU-10 §90: role from root.roles.byUser[userId]; fallback to p.roles for legacy data.
  const byUser = root?.roles?.byUser || {}
  const groups = {}
  for (const p of (people || [])) {
    const legacyRoles = Array.isArray(p.roles) && p.roles.length > 0 ? p.roles : null
    const role = legacyRoles ? legacyRoles[0] : (byUser[p.userId] || 'member')
    if (!groups[role]) groups[role] = []
    groups[role].push(p)
  }
  // Order by the roles enum order, then any extras
  const ordered = available.length
    ? available.map(r => r.name)
    : ['superadmin', 'admin', 'manager', 'member', 'advisor', 'investor']
  return ordered
    .filter(r => groups[r]?.length)
    .map(r => {
      const meta = available.find(m => m.name === r) || {}
      return {
        role: r,
        label: meta.label || r.replace(/^./, c => c.toUpperCase()),
        color: meta.color || 'gray',
        users: groups[r].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      }
    })
}

export const rowToPerson = (r) => ({
  name: r.name,
  email: r.email,
  avatar: r.avatar,
  position: r.position,
  location: r.location,
  country: r.country,
  city: r.city,
  timezone: r.timezone,
  github: r.github,
  weeklyHours: r.weekly_hours,
  workingDays: r.working_days,
  workingHours: { start: r.working_hours_start, end: r.working_hours_end },
  // CU-10 §90: roles/isAdmin removed from people view. Role data lives in
  // root.roles.byUser[userId] (server GET /auth/org/:orgId/member-roles).
  origin_valuation: r.origin_valuation
})
